import { useTranslation } from 'react-i18next';
import { Button, Modal, type ModalProps, Text } from '@mantine/core';
import { ModalFooter } from '@/components/ModalFooter/ModalFooter';
import { useScreenSize } from '@/hooks';
import styles from './ConfirmationModal.module.sass';
import type { ExtendedCustomColors } from '@/types';

type ConfirmationModalProps = {
  opened: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  color?: ExtendedCustomColors;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
} & Omit<ModalProps, 'opened' | 'onClose' | 'title' | 'children'>;

export const ConfirmationModal = ({
  opened,
  title,
  message,
  confirmLabel,
  cancelLabel,
  color = 'base',
  isLoading = false,
  onConfirm,
  onCancel,
  onClose,
  ...modalProps
}: ConfirmationModalProps) => {
  const { t } = useTranslation('global');
  const { isDesktop } = useScreenSize();

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="sm" fullScreen={!isDesktop} centered {...modalProps}>
      <div>
        <Text className={styles.message}>{message}</Text>
      </div>

      <ModalFooter
        secondaryActions={[
          <Button key="cancel" variant="outline" size="sm" onClick={onCancel}>
            {cancelLabel || t('cancelButtonLabel')}
          </Button>,
        ]}
        mainAction={
          <Button variant="filled" color={color} size="sm" onClick={onConfirm} disabled={isLoading}>
            {confirmLabel || t('confirmButtonLabel')}
          </Button>
        }
      />
    </Modal>
  );
};
